/**
 * AdminGlossary — /admin/glossary. Firm-wide acronym + jargon
 * dictionary that Claude expands on first use during Transform.
 *
 * Columns:
 *   - Term · Expansion (click-to-edit) · Kind · Used in N meetings ·
 *     Added by · Added · Firmwide · Actions (audit / delete)
 *
 * Filters:
 *   - Search box matches term + expansion
 *   - Kind chips: All · Acronyms · Jargon (single-select toggle)
 *
 * Top actions:
 *   - Inline form to add a new term
 *   - "Seed common AEC acronyms" — bulk-inserts ~24 standard terms
 *   - "Paste CSV" modal — bulk-import from CSV (term,expansion,kind)
 *
 * Sorting:
 *   - Click any sortable header. Default: usage count desc (most-
 *     used first, so the busiest expansions surface).
 *
 * Audit:
 *   - Per-row Audit button opens a modal with humanized history
 *     (created / patched / deleted / seeded / csv-imported), with
 *     before/after JSON behind a "Show raw" toggle.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  History,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { api, ApiError } from '../../lib/apiClient.ts';
import { NO_AUTOFILL } from '../../lib/noAutofill.ts';
import { AttendeeAvatar } from '../../components/editor/AttendeeAvatar.tsx';

interface GlossaryTerm {
  id: string;
  term: string;
  expansion: string;
  kind: 'acronym' | 'jargon';
  usageCount: number;
  isFirmwide: boolean;
  createdAt: string;
  addedByUserId?: string | null;
  addedByName?: string | null;
  addedByEmail?: string | null;
  meetingCount?: number;
}

type SortKey = 'term' | 'expansion' | 'kind' | 'meetings' | 'addedBy' | 'created';
type SortDir = 'asc' | 'desc';
type KindFilter = 'all' | 'acronym' | 'jargon';

export function AdminGlossary() {
  const qc = useQueryClient();
  const [newTerm, setNewTerm] = useState('');
  const [newExpansion, setNewExpansion] = useState('');
  const [newKind, setNewKind] = useState<'acronym' | 'jargon'>('acronym');
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('meetings');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [seedOpen, setSeedOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [auditTarget, setAuditTarget] = useState<GlossaryTerm | null>(null);

  const list = useQuery({
    queryKey: ['glossary', 'admin'],
    queryFn: () => api.get<{ terms: GlossaryTerm[] }>('/glossary'),
  });

  const add = useMutation({
    mutationFn: () =>
      api.post<{ term: GlossaryTerm; reused: boolean }>('/glossary', {
        term: newTerm.trim(),
        expansion: newExpansion.trim(),
        kind: newKind,
      }),
    onSuccess: () => {
      setNewTerm('');
      setNewExpansion('');
      qc.invalidateQueries({ queryKey: ['glossary'] });
    },
  });

  const patch = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Partial<{
        term: string;
        expansion: string;
        kind: 'acronym' | 'jargon';
        isFirmwide: boolean;
      }>;
    }) => api.patch<{ term: GlossaryTerm }>(`/glossary/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['glossary'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/glossary/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['glossary'] });
    },
  });

  const seedAec = useMutation({
    mutationFn: () =>
      api.post<{ inserted: number; skipped: number; skippedTerms: string[] }>(
        '/glossary/seed-aec',
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['glossary'] });
      setSeedOpen(false);
    },
  });

  const importCsv = useMutation({
    mutationFn: (csv: string) =>
      api.post<{
        inserted: number;
        updated: number;
        errors: Array<{ line: number; row: string; error: string }>;
      }>('/glossary/import-csv', { csv }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['glossary'] });
    },
  });

  const rows = list.data?.terms ?? [];

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = rows.filter((t) => {
      if (kindFilter !== 'all' && t.kind !== kindFilter) return false;
      if (q) {
        return (
          t.term.toLowerCase().includes(q) ||
          t.expansion.toLowerCase().includes(q)
        );
      }
      return true;
    });
    out = [...out].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortKey) {
        case 'term':
          return a.term.localeCompare(b.term) * dir;
        case 'expansion':
          return a.expansion.localeCompare(b.expansion) * dir;
        case 'kind':
          return a.kind.localeCompare(b.kind) * dir;
        case 'meetings':
          return ((a.meetingCount ?? 0) - (b.meetingCount ?? 0)) * dir;
        case 'addedBy':
          return (a.addedByName ?? '').localeCompare(b.addedByName ?? '') * dir;
        case 'created':
          return (
            (new Date(a.createdAt).getTime() -
              new Date(b.createdAt).getTime()) *
            dir
          );
        default:
          return 0;
      }
    });
    return out;
  }, [rows, search, kindFilter, sortKey, sortDir]);

  const onSort = (k: SortKey) => {
    if (k === sortKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(k);
      // Numeric / date columns descend first; text ascends.
      setSortDir(
        k === 'meetings' || k === 'created' ? 'desc' : 'asc',
      );
    }
  };

  const addErr =
    add.error instanceof ApiError
      ? add.error.message
      : add.error instanceof Error
        ? add.error.message
        : null;

  return (
    <div className="space-y-4">
      {/* Mockup header — 18px charcoal title + 13px charcoal/65 sub
          + .admin-btn--pine-outline (Seed) + .admin-btn--outline
          (Paste CSV) on the right. */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-[18px] font-semibold text-charcoal">Firm glossary</h2>
          <p className="mt-1 max-w-[60ch] text-[13px] text-charcoal/65">
            Acronyms and jargon Claude expands on first use during Transform
            (e.g. <em>SDP</em> →{' '}
            <em>Site Development Plan (SDP)</em>). Only terms that appear in
            a meeting&rsquo;s raw input get sent to Claude, so the full list
            doesn&rsquo;t inflate token cost.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setSeedOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-pine bg-transparent px-3.5 py-1.5 text-[12px] font-medium text-pine transition-colors hover:bg-pine/[0.08]"
          >
            <Sparkles size={13} />
            Seed AEC acronyms
          </button>
          <button
            type="button"
            onClick={() => setCsvOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-charcoal/20 bg-transparent px-3.5 py-1.5 text-[12px] font-medium text-charcoal transition-colors hover:border-pine hover:bg-pine/[0.08] hover:text-pine"
          >
            <Upload size={13} />
            Paste CSV
          </button>
        </div>
      </div>

      {/* Add form — same chrome family as the AdminMeetings filter
          card (cloud + charcoal/10 + rounded-[14px] + 14/16 padding). */}
      <form
        className="flex flex-wrap items-end gap-2 rounded-[14px] border border-charcoal/10 bg-cloud px-4 py-3.5"
        onSubmit={(e) => {
          e.preventDefault();
          if (!newTerm.trim() || !newExpansion.trim()) return;
          add.mutate();
        }}
      >
        <label className="flex w-32 flex-col gap-1 text-[11px]">
          <span className="font-semibold uppercase tracking-[0.14em] text-charcoal/60">
            Term
          </span>
          <input
            {...NO_AUTOFILL}
            type="text"
            value={newTerm}
            onChange={(e) => setNewTerm(e.target.value)}
            placeholder="SDP"
            className="rounded-[8px] border border-charcoal/20 bg-bone px-2.5 py-1.5 text-[12px] text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
            maxLength={120}
          />
        </label>
        <label className="flex flex-1 flex-col gap-1 text-[11px]">
          <span className="font-semibold uppercase tracking-[0.14em] text-charcoal/60">
            Expansion
          </span>
          <input
            {...NO_AUTOFILL}
            type="text"
            value={newExpansion}
            onChange={(e) => setNewExpansion(e.target.value)}
            placeholder="Site Development Plan"
            className="rounded-[8px] border border-charcoal/20 bg-bone px-2.5 py-1.5 text-[12px] text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
            maxLength={500}
          />
        </label>
        <label className="flex w-28 flex-col gap-1 text-[11px]">
          <span className="font-semibold uppercase tracking-[0.14em] text-charcoal/60">
            Kind
          </span>
          <select
            value={newKind}
            onChange={(e) =>
              setNewKind(e.target.value as 'acronym' | 'jargon')
            }
            className="rounded-[8px] border border-charcoal/20 bg-bone px-2.5 py-1.5 text-[12px] text-charcoal focus:border-pine focus:outline-none focus:ring-[3px] focus:ring-pine/15"
          >
            <option value="acronym">Acronym</option>
            <option value="jargon">Jargon</option>
          </select>
        </label>
        <button
          type="submit"
          disabled={add.isPending || !newTerm.trim() || !newExpansion.trim()}
          className="inline-flex items-center gap-1.5 rounded-full bg-pine px-4 py-1.5 text-[12px] font-medium text-cream transition-colors hover:bg-pine-deep disabled:opacity-50"
        >
          <Plus size={13} />
          {add.isPending ? 'Adding…' : 'Add'}
        </button>
      </form>

      {addErr && (
        <div className="rounded-2xl border border-terra-cotta/30 bg-terra-cotta/10 px-4 py-3 text-[13px] text-terra-cotta">
          {addErr}
        </div>
      )}

      {seedAec.isSuccess && seedAec.data && (
        <div className="rounded-2xl border border-pine/25 bg-pine/[0.06] px-4 py-3 text-[13px] text-pine">
          ✓ Seeded <b>{seedAec.data.inserted}</b> AEC acronym
          {seedAec.data.inserted === 1 ? '' : 's'}.{' '}
          {seedAec.data.skipped > 0 && (
            <>
              Skipped {seedAec.data.skipped} that already existed (
              {seedAec.data.skippedTerms.slice(0, 6).join(', ')}
              {seedAec.data.skippedTerms.length > 6 ? '…' : ''}).
            </>
          )}
        </div>
      )}
      {importCsv.isSuccess && importCsv.data && (
        <div className="rounded-2xl border border-pine/25 bg-pine/[0.06] px-4 py-3 text-[13px] text-pine">
          ✓ CSV import: <b>{importCsv.data.inserted}</b> new ·{' '}
          <b>{importCsv.data.updated}</b> updated
          {importCsv.data.errors.length > 0 && (
            <>
              {' '}
              ·{' '}
              <span className="text-terra-cotta">
                {importCsv.data.errors.length} error
                {importCsv.data.errors.length === 1 ? '' : 's'}
              </span>
            </>
          )}
          .
        </div>
      )}

      {/* Search pill — same .search-pill family as the other admin
          tables. Kind chips below match the .filter-chip family. */}
      <div className="space-y-2">
        <div className="relative">
          <Search
            size={16}
            aria-hidden
            className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/45"
          />
          <input
            {...NO_AUTOFILL}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search terms or expansions…"
            className="w-full rounded-[14px] border border-charcoal/15 bg-cloud/60 py-3 pl-11 pr-4 text-[13px] text-charcoal placeholder:text-charcoal/45 focus:border-pine focus:bg-bone focus:outline-none focus:ring-[3px] focus:ring-pine/15"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <KindChip
            active={kindFilter === 'all'}
            onClick={() => setKindFilter('all')}
          >
            All
            <span className="ml-1 text-charcoal/50">{rows.length}</span>
          </KindChip>
          <KindChip
            active={kindFilter === 'acronym'}
            onClick={() => setKindFilter('acronym')}
          >
            Acronyms
            <span className={'ml-1 ' + (kindFilter === 'acronym' ? 'opacity-80' : 'text-charcoal/50')}>
              {rows.filter((r) => r.kind === 'acronym').length}
            </span>
          </KindChip>
          <KindChip
            active={kindFilter === 'jargon'}
            onClick={() => setKindFilter('jargon')}
          >
            Jargon
            <span className={'ml-1 ' + (kindFilter === 'jargon' ? 'opacity-80' : 'text-charcoal/50')}>
              {rows.filter((r) => r.kind === 'jargon').length}
            </span>
          </KindChip>
          <span className="ml-auto text-[11px] text-charcoal/55">
            {visible.length} of {rows.length} term
            {rows.length === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      {/* ── Empty / loading states ──────────────────────────── */}
      {list.isLoading && (
        <div className="py-8 text-center text-sm italic text-charcoal/55">
          Loading…
        </div>
      )}
      {list.isError && (
        <div className="rounded-2xl border border-terra-cotta/30 bg-terra-cotta/10 p-4 text-[13px] text-terra-cotta">
          {String((list.error as Error).message)}
        </div>
      )}
      {!list.isLoading && rows.length === 0 && (
        <div className="rounded-[14px] border border-dashed border-charcoal/20 bg-cloud/40 px-4 py-10 text-center text-[12px] italic text-charcoal/55">
          No glossary entries yet. Use the form above, or click{' '}
          <b>Seed AEC acronyms</b> to start with the common ones (SDP, SD, DD,
          CD, CA, LEED, RFI, etc.).
        </div>
      )}

      {/* Table — same .admin-table family as the other admin tables. */}
      {visible.length > 0 && (
        <div className="overflow-x-auto rounded-[14px] border border-charcoal/8 bg-bone shadow-[0_1px_2px_rgba(43,42,47,0.04)]">
          <table className="w-full text-[13px]">
            <thead className="border-b border-charcoal/10 bg-cloud/60 text-left text-[10px] font-bold uppercase tracking-[0.14em] text-charcoal/65">
              <tr>
                <Th sortKey="term" current={sortKey} dir={sortDir} onSort={onSort}>
                  Term
                </Th>
                <Th
                  sortKey="expansion"
                  current={sortKey}
                  dir={sortDir}
                  onSort={onSort}
                >
                  Expansion
                </Th>
                <Th sortKey="kind" current={sortKey} dir={sortDir} onSort={onSort}>
                  Kind
                </Th>
                <Th
                  sortKey="meetings"
                  current={sortKey}
                  dir={sortDir}
                  onSort={onSort}
                  align="right"
                >
                  Used in
                </Th>
                <Th
                  sortKey="addedBy"
                  current={sortKey}
                  dir={sortDir}
                  onSort={onSort}
                >
                  Added by
                </Th>
                <Th
                  sortKey="created"
                  current={sortKey}
                  dir={sortDir}
                  onSort={onSort}
                >
                  Added
                </Th>
                <th className="px-3.5 py-2.5 text-right" />
              </tr>
            </thead>
            <tbody>
              {visible.map((t) => (
                <GlossaryRow
                  key={t.id}
                  term={t}
                  onPatch={(body) => patch.mutate({ id: t.id, body })}
                  onDelete={() => {
                    if (confirm(`Delete glossary entry "${t.term}"?`)) {
                      remove.mutate(t.id);
                    }
                  }}
                  onAudit={() => setAuditTarget(t)}
                  isPending={patch.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modals ─────────────────────────────────────────── */}
      {seedOpen && (
        <SeedConfirmModal
          onCancel={() => setSeedOpen(false)}
          onConfirm={() => seedAec.mutate()}
          isPending={seedAec.isPending}
        />
      )}
      {csvOpen && (
        <CsvImportModal
          onCancel={() => setCsvOpen(false)}
          onImport={(csv) => importCsv.mutate(csv)}
          isPending={importCsv.isPending}
          result={importCsv.data ?? null}
          error={
            importCsv.error instanceof ApiError
              ? importCsv.error.message
              : importCsv.error instanceof Error
                ? importCsv.error.message
                : null
          }
        />
      )}
      {auditTarget && (
        <GlossaryAuditModal
          term={auditTarget}
          onClose={() => setAuditTarget(null)}
        />
      )}
    </div>
  );
}

/* ─── Row ───────────────────────────────────────────────────────── */

function GlossaryRow({
  term,
  onPatch,
  onDelete,
  onAudit,
  isPending,
}: {
  term: GlossaryTerm;
  onPatch: (
    body: Partial<{
      term: string;
      expansion: string;
      kind: 'acronym' | 'jargon';
      isFirmwide: boolean;
    }>,
  ) => void;
  onDelete: () => void;
  onAudit: () => void;
  isPending: boolean;
}) {
  const [editing, setEditing] = useState<null | 'term' | 'expansion' | 'kind'>(null);
  const [draft, setDraft] = useState('');
  const start = (field: 'term' | 'expansion' | 'kind', current: string) => {
    setEditing(field);
    setDraft(current);
  };
  const commit = () => {
    if (!editing) return;
    const t = draft.trim();
    if (!t) {
      setEditing(null);
      return;
    }
    if (editing === 'kind') {
      if (t === term.kind) {
        setEditing(null);
        return;
      }
      onPatch({ kind: t as 'acronym' | 'jargon' });
    } else {
      if (t === term[editing]) {
        setEditing(null);
        return;
      }
      onPatch({ [editing]: t });
    }
    setEditing(null);
  };
  const cancel = () => setEditing(null);

  return (
    // Same row chrome as AdminUsers / AdminMeetings: charcoal/[0.06]
    // hairline divider + pine/[0.04] hover wash.
    <tr className="border-b border-charcoal/[0.06] last:border-b-0 transition-colors hover:bg-pine/[0.04]">
      {/* Term */}
      <td className="px-3.5 py-3">
        {editing === 'term' ? (
          <InlineEdit
            draft={draft}
            setDraft={setDraft}
            commit={commit}
            cancel={cancel}
            disabled={isPending}
            mono
          />
        ) : (
          <button
            type="button"
            onClick={() => start('term', term.term)}
            className="rounded px-1 -mx-1 font-mono text-[13px] font-semibold text-charcoal hover:bg-pine/5 hover:text-pine"
            title="Click to edit"
          >
            {term.term}
          </button>
        )}
      </td>

      {/* Expansion */}
      <td className="px-3.5 py-3">
        {editing === 'expansion' ? (
          <InlineEdit
            draft={draft}
            setDraft={setDraft}
            commit={commit}
            cancel={cancel}
            disabled={isPending}
          />
        ) : (
          <button
            type="button"
            onClick={() => start('expansion', term.expansion)}
            className="group/expand inline-flex items-center gap-1.5 rounded px-1 -mx-1 text-left text-charcoal hover:bg-pine/5 hover:text-pine"
            title="Click to edit"
          >
            <span>{term.expansion}</span>
            <Pencil
              size={11}
              className="text-charcoal/30 opacity-0 transition-opacity group-hover/expand:opacity-100"
            />
          </button>
        )}
      </td>

      {/* Kind */}
      <td className="px-3.5 py-3">
        {editing === 'kind' ? (
          <select
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') cancel();
            }}
            disabled={isPending}
            className="rounded-lg border border-charcoal/20 bg-paper-light px-2 py-1 text-xs text-charcoal focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20"
          >
            <option value="acronym">Acronym</option>
            <option value="jargon">Jargon</option>
          </select>
        ) : (
          <button
            type="button"
            onClick={() => start('kind', term.kind)}
            // Mockup .status-badge family — 10px bold tracking-[0.10em].
            // Acronym → pine/10 + pine, Jargon → amber/30 + amber-900.
            className={
              'rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.10em] transition-colors hover:ring-1 hover:ring-pine ' +
              (term.kind === 'acronym'
                ? 'bg-pine/10 text-pine'
                : 'bg-amber-300/30 text-amber-900')
            }
            title="Click to change kind"
          >
            {term.kind}
          </button>
        )}
      </td>

      {/* Used in N meetings */}
      <td className="px-3.5 py-3 text-right tabular-nums text-[12px] text-charcoal">
        {term.meetingCount === 0 || term.meetingCount === undefined ? (
          <span className="text-charcoal/40">-</span>
        ) : (
          <>
            <b className="text-pine">{term.meetingCount}</b>
            <span className="ml-1 text-[10px] text-charcoal/50">
              meeting{term.meetingCount === 1 ? '' : 's'}
            </span>
          </>
        )}
      </td>

      {/* Added by */}
      <td className="px-3.5 py-3">
        {term.addedByName ? (
          <div className="flex items-center gap-2 text-[12px]">
            <AttendeeAvatar
              name={term.addedByName}
              headshotUrl={null}
              size={20}
            />
            <span className="truncate text-charcoal">{term.addedByName}</span>
          </div>
        ) : (
          <span className="text-[11px] italic text-charcoal/40">-</span>
        )}
      </td>

      {/* Added (created date) */}
      <td className="px-3.5 py-3 text-[11px] text-charcoal/60">
        {new Date(term.createdAt).toLocaleDateString()}
        {term.isFirmwide && (
          <span className="ml-2 rounded-full bg-pine/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-pine">
            firmwide
          </span>
        )}
      </td>

      {/* Actions */}
      <td className="px-3.5 py-3 text-right">
        <div className="flex items-center justify-end gap-1.5">
          {/* Mockup .icon-btn-sm — cream + charcoal/15, hover pine
              (Audit) or terra-cotta (Delete). */}
          <button
            type="button"
            onClick={onAudit}
            title="View audit history"
            aria-label="Audit"
            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-charcoal/15 bg-cream text-charcoal/60 transition-all hover:border-pine hover:bg-pine/[0.08] hover:text-pine"
          >
            <History size={13} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            title="Delete term"
            aria-label="Delete"
            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-charcoal/15 bg-cream text-charcoal/60 transition-all hover:border-terra-cotta hover:bg-terra-cotta/[0.08] hover:text-terra-cotta"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </td>
    </tr>
  );
}

function InlineEdit({
  draft,
  setDraft,
  commit,
  cancel,
  disabled,
  mono = false,
}: {
  draft: string;
  setDraft: (v: string) => void;
  commit: () => void;
  cancel: () => void;
  disabled: boolean;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <input
        {...NO_AUTOFILL}
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') cancel();
        }}
        disabled={disabled}
        className={
          'w-full rounded-lg border border-charcoal/20 bg-paper-light px-2 py-1 text-xs text-charcoal focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20 ' +
          (mono ? 'font-mono font-semibold' : '')
        }
      />
      <button
        type="button"
        onClick={commit}
        disabled={disabled}
        className="rounded-full bg-pine p-1 text-cloud transition-colors hover:bg-pine/90 disabled:opacity-50"
        title="Save (Enter)"
      >
        <Check size={11} />
      </button>
      <button
        type="button"
        onClick={cancel}
        disabled={disabled}
        className="rounded-full border border-charcoal/25 p-1 text-charcoal transition-colors hover:border-pine hover:text-pine disabled:opacity-50"
        title="Cancel (Esc)"
      >
        <X size={11} />
      </button>
    </div>
  );
}

/* ─── Sort header ──────────────────────────────────────────────── */

function Th({
  sortKey: key,
  current,
  dir,
  onSort,
  align = 'left',
  children,
}: {
  sortKey: SortKey;
  current: SortKey;
  dir: SortDir;
  onSort: (k: SortKey) => void;
  align?: 'left' | 'right';
  children: ReactNode;
}) {
  // Same .admin-table thead th spec as the other admin tables.
  const isActive = current === key;
  return (
    <th
      className={
        'cursor-pointer select-none px-3.5 py-2.5 transition-colors hover:text-pine ' +
        (align === 'right' ? 'text-right ' : '') +
        (isActive ? 'text-pine' : '')
      }
      onClick={() => onSort(key)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {isActive && (
          <span className="text-[9px] leading-none">
            {dir === 'asc' ? '▲' : '▼'}
          </span>
        )}
      </span>
    </th>
  );
}

/* ─── Kind chip ────────────────────────────────────────────────── */

function KindChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  // Same .filter-chip family as the other admin pages.
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'rounded-full border px-3 py-1 text-[12px] font-medium transition-colors ' +
        (active
          ? 'border-pine bg-pine text-cream'
          : 'border-charcoal/20 bg-cloud/60 text-charcoal hover:border-pine hover:text-pine')
      }
    >
      {children}
    </button>
  );
}

/* ─── Seed AEC modal ───────────────────────────────────────────── */

function SeedConfirmModal({
  onCancel,
  onConfirm,
  isPending,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={onCancel}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-charcoal/12 bg-bone shadow-[0_18px_40px_rgba(43,42,47,0.18)]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="border-b border-charcoal/10 px-5 py-4">
          <h2 className="text-[12px] font-bold uppercase tracking-[0.18em] text-pine">
            Seed AEC acronyms
          </h2>
          <p className="mt-1 text-[12px] text-charcoal/65">
            Inserts ~24 standard AEC acronyms in one shot - design phases
            (SD, DD, CD, CA), credentials (PLA, ASLA, AIA), tools (BIM,
            CAD), and process terms (RFI, RFP). Existing terms are
            skipped, never overwritten.
          </p>
        </div>
        <div className="px-5 py-4 text-sm">
          <ul className="grid grid-cols-3 gap-x-3 gap-y-1 text-[11px] text-charcoal/70">
            {[
              'SDP', 'SD', 'DD', 'CD', 'CA', 'PD',
              'RFI', 'RFP', 'RFQ', 'OAC', 'GC', 'MEP',
              'LEED', 'SITES', 'BIM', 'CAD', 'FF&E', 'ROW',
              'HOA', 'ADA', 'ASLA', 'AIA', 'PLA', 'FASLA',
            ].map((t) => (
              <li key={t} className="font-mono font-semibold text-charcoal">
                {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-charcoal/10 px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-full border border-charcoal/25 px-4 py-2 text-[13px] font-medium text-charcoal transition-colors hover:border-pine hover:text-pine"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="rounded-full bg-pine px-4 py-2 text-[13px] font-medium text-cream transition-colors hover:bg-pine-deep disabled:opacity-50"
          >
            {isPending ? 'Seeding…' : 'Seed acronyms'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── CSV import modal ─────────────────────────────────────────── */

function CsvImportModal({
  onCancel,
  onImport,
  isPending,
  result,
  error,
}: {
  onCancel: () => void;
  onImport: (csv: string) => void;
  isPending: boolean;
  result: {
    inserted: number;
    updated: number;
    errors: Array<{ line: number; row: string; error: string }>;
  } | null;
  error: string | null;
}) {
  const [csv, setCsv] = useState('');
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={onCancel}
    >
      <div
        className="w-full max-w-xl rounded-2xl border border-charcoal/12 bg-bone shadow-[0_18px_40px_rgba(43,42,47,0.18)]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="border-b border-charcoal/10 px-5 py-4">
          <h2 className="text-[12px] font-bold uppercase tracking-[0.18em] text-pine">
            Paste CSV
          </h2>
          <p className="mt-1 text-[12px] text-charcoal/65">
            One row per line: <code className="rounded bg-paper-light px-1">term,expansion,kind</code>.
            Kind is <code className="rounded bg-paper-light px-1">acronym</code> or{' '}
            <code className="rounded bg-paper-light px-1">jargon</code> (defaults
            to acronym). Existing terms are upserted.
          </p>
        </div>
        <div className="px-5 py-4 text-sm">
          <textarea
            {...NO_AUTOFILL}
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            rows={10}
            placeholder={`SDP,Site Development Plan,acronym\nLEED,Leadership in Energy and Environmental Design,acronym\ndaylighting,The use of natural light to illuminate interiors,jargon`}
            className="w-full resize-y rounded-lg border border-charcoal/20 bg-paper-light px-3 py-2 font-mono text-[12px] text-charcoal placeholder:text-charcoal/40 focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20"
          />
          {error && (
            <div className="mt-2 rounded-2xl border border-terra-cotta/30 bg-terra-cotta/10 px-3 py-2 text-[12px] text-terra-cotta">
              {error}
            </div>
          )}
          {result && (
            <div className="mt-3 space-y-2 text-[11px]">
              <div className="rounded-lg border border-pine/30 bg-pine/5 px-3 py-2 text-pine">
                ✓ <b>{result.inserted}</b> new · <b>{result.updated}</b>{' '}
                updated
                {result.errors.length > 0 && (
                  <>
                    {' '}
                    ·{' '}
                    <span className="text-red-700">
                      {result.errors.length} error
                      {result.errors.length === 1 ? '' : 's'}
                    </span>
                  </>
                )}
              </div>
              {result.errors.length > 0 && (
                <ul className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                  {result.errors.slice(0, 10).map((e, i) => (
                    <li key={i}>
                      Line {e.line}: <code>{e.row}</code> - {e.error}
                    </li>
                  ))}
                  {result.errors.length > 10 && (
                    <li className="italic">
                      …{result.errors.length - 10} more
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-charcoal/10 px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-full border border-charcoal/25 px-4 py-2 text-[13px] font-medium text-charcoal transition-colors hover:border-pine hover:text-pine"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() => onImport(csv)}
            disabled={!csv.trim() || isPending}
            className="rounded-full bg-pine px-4 py-2 text-[13px] font-medium text-cream transition-colors hover:bg-pine-deep disabled:opacity-50"
          >
            {isPending ? 'Importing…' : 'Import'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Audit modal ───────────────────────────────────────────── */

interface AuditEntry {
  id: number;
  action: string;
  before: unknown;
  after: unknown;
  createdAt: string;
  adminUserId: string;
  adminName: string | null;
  adminEmail: string | null;
}

function GlossaryAuditModal({
  term,
  onClose,
}: {
  term: GlossaryTerm;
  onClose: () => void;
}) {
  const auditQ = useQuery({
    queryKey: ['glossary', 'audit', term.id],
    queryFn: () =>
      api.get<{ entries: AuditEntry[] }>(`/glossary/${term.id}/audit`),
  });
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-xl rounded-2xl border border-charcoal/12 bg-bone shadow-[0_18px_40px_rgba(43,42,47,0.18)]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="border-b border-charcoal/10 px-5 py-4">
          <h2 className="text-[12px] font-bold uppercase tracking-[0.18em] text-pine">
            Audit log - {term.term}
          </h2>
          <p className="mt-1 text-[12px] text-charcoal/65">
            All admin actions taken on this glossary entry: created,
            edited, deleted, seeded, or imported via CSV.
          </p>
        </div>
        <div className="max-h-[60vh] overflow-y-auto px-5 py-3 text-sm">
          {auditQ.isLoading && (
            <div className="py-4 text-center italic text-charcoal/50">
              Loading…
            </div>
          )}
          {auditQ.data && auditQ.data.entries.length === 0 && (
            <div className="py-4 text-center italic text-charcoal/50">
              No audit history.
            </div>
          )}
          {auditQ.data && auditQ.data.entries.length > 0 && (
            <ul className="divide-y divide-charcoal/10">
              {auditQ.data.entries.map((e) => (
                <AuditRow key={e.id} entry={e} />
              ))}
            </ul>
          )}
        </div>
        <div className="flex items-center justify-end border-t border-charcoal/10 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-charcoal/25 px-4 py-2 text-[13px] font-medium text-charcoal transition-colors hover:border-pine hover:text-pine"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function AuditRow({ entry }: { entry: AuditEntry }) {
  const [showRaw, setShowRaw] = useState(false);
  const summary = summarizeGlossaryAudit(entry);
  const hasDiff = !!entry.before || !!entry.after;
  return (
    <li className="py-2.5">
      <div className="flex items-baseline justify-between gap-2 text-[13px]">
        <span className="font-medium text-charcoal">{summary.label}</span>
        <span className="text-[11px] text-charcoal/50">
          {new Date(entry.createdAt).toLocaleString()}
        </span>
      </div>
      {summary.detail && (
        <div className="mt-0.5 text-[12px] text-charcoal/70">
          {summary.detail}
        </div>
      )}
      <div className="mt-1 flex items-center justify-between gap-2">
        <div className="text-[11px] text-charcoal/55">
          by {entry.adminName ?? entry.adminEmail ?? '(unknown)'}
          <span className="ml-1.5 rounded-full bg-charcoal/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-charcoal/50">
            {entry.action}
          </span>
        </div>
        {hasDiff && (
          <button
            type="button"
            onClick={() => setShowRaw((v) => !v)}
            className="text-[11px] text-charcoal/45 hover:text-pine"
          >
            {showRaw ? 'Hide raw' : 'Show raw'}
          </button>
        )}
      </div>
      {showRaw && hasDiff && (
        <pre className="mt-2 overflow-x-auto rounded-md bg-paper-light p-2 text-[10px] text-charcoal/70">
          {JSON.stringify({ before: entry.before, after: entry.after }, null, 2)}
        </pre>
      )}
    </li>
  );
}

function summarizeGlossaryAudit(entry: AuditEntry): {
  label: string;
  detail: string | null;
} {
  switch (entry.action) {
    case 'glossary.create':
      return { label: 'Created', detail: 'New entry added to the dictionary.' };
    case 'glossary.update':
      return {
        label: 'Updated via re-add',
        detail: 'Existing term replaced when someone re-added the same term.',
      };
    case 'glossary.patch': {
      const before = (entry.before as Record<string, unknown>) ?? {};
      const after = (entry.after as Record<string, unknown>) ?? {};
      const changes: string[] = [];
      for (const k of Object.keys(after)) {
        changes.push(`${k}: "${String(before[k] ?? '-')}" → "${String(after[k])}"`);
      }
      return {
        label: 'Edited',
        detail: changes.length > 0 ? changes.join(' · ') : null,
      };
    }
    case 'glossary.delete':
      return {
        label: 'Deleted',
        detail: 'Entry removed from the dictionary.',
      };
    case 'glossary.seed-aec':
      return {
        label: 'Seeded',
        detail: 'Added via the AEC starter pack.',
      };
    case 'glossary.import-csv':
      return { label: 'Imported (CSV)', detail: null };
    case 'glossary.import-csv-update':
      return {
        label: 'Updated via CSV import',
        detail: null,
      };
    default:
      return { label: entry.action, detail: null };
  }
}
